import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  /** Whether the banner is currently visible. Parent toggles this on rate-limit and on
   *  next successful request. */
  visible: boolean;
  /** Called by the banner when its 10-second auto-dismiss timer expires. */
  onDismiss: () => void;
}

/** How long the banner stays on screen before auto-dismissing. */
const AUTO_DISMISS_MS = 10_000;

/**
 * Soft banner shown at the top of browse pages (Jobs / Companies) when retry-with-backoff
 * has exhausted its attempts and the user-visible request still failed with a 429.
 *
 * Mini Phase 6.5 dropped the countdown display: now that browse GETs auto-retry, the user
 * rarely sees this banner — and when they do, the right hint is "try again in a moment",
 * not "wait exactly N seconds". Banner auto-dismisses after 10s, OR sooner if the parent
 * clears it (typically on the next successful request).
 *
 * Page content already loaded stays visible behind the banner; this is a soft notice, not
 * a blocker.
 *
 * Forms (signin / signup / forgot-password / change-password) use `RateLimitInline` instead,
 * which keeps the active countdown — appropriate because the user is mid-action and needs
 * to know when to retry.
 */
export function RateLimitBanner({ visible, onDismiss }: Props) {
  useEffect(() => {
    if (!visible) return;
    const id = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [visible, onDismiss]);

  if (!visible) return null;

  return (
    <div
      role="status"
      className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm"
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <p>Some content is loading slowly. Please try again in a moment.</p>
    </div>
  );
}

interface InlineProps {
  /** Unix ms when the rate-limit window expires; null disables. */
  deadlineMs: number | null;
  className?: string;
}

/**
 * Inline countdown for auth forms. Reads the deadline and renders a tiny "Try again in Ns"
 * line; the parent form should also disable its submit button while the deadline is in
 * the future.
 */
export function RateLimitInline({ deadlineMs, className }: InlineProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadlineMs === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [deadlineMs]);

  if (deadlineMs === null) return null;
  const remaining = Math.max(0, Math.ceil((deadlineMs - now) / 1000));
  if (remaining === 0) return null;

  return (
    <p className={className ?? 'text-sm text-amber-900 font-medium'}>
      You&apos;ve made too many attempts. Please wait{' '}
      <span className="font-bold">{remaining} second{remaining === 1 ? '' : 's'}</span> and try again.
    </p>
  );
}

/** True while the deadline is still in the future. Forms use this to disable submit. */
export function isRateLimited(deadlineMs: number | null): boolean {
  return deadlineMs !== null && deadlineMs > Date.now();
}
